import { createContext, useContext } from "react";
import { MixpanelEventName } from "@portal/app/_common/types/mixpanel";
import type { RequestOptions } from "mixpanel-browser";
import type { FlowStepDescriptor } from "@portal/app/_common/lib/flowStepTracking";
import type {
  HostedFlowTrackingPhase,
  HostedFlowTrackingStep,
} from "../utils/flowStepDescriptors";

export interface TrackingIds {
  // Core IDs
  customerId: string | null;
  marketplaceEventId: string | null;

  // Compliance IDs
  jornayaLeadId: string | null;
  trustedFormCertId: string | null;
  everflowTransactionId: string | null;

  // Facebook cookies
  facebookClickId: string | null;
  facebookBrowserId: string | null;

  googleClickId?: string;
  googleWbraid?: string;
  googleGbraid?: string;
  googleAdsCapturedAt?: string;

  // Google Analytics
  gaClientId: string | null;

  // Everflow affiliate params
  everflowOfferId: string | null;
  everflowAffiliateId: string | null;
  everflowSub1: string | null;
  everflowSub2: string | null;
  everflowSub3: string | null;
  everflowSub4: string | null;
  everflowSub5: string | null;
  everflowSub6: string | null;
  everflowSub7: string | null;
  everflowSub8: string | null;
  everflowUid: string | null;
  everflowSourceId: string | null;
}

export interface FormFunnelTrackingContextType {
  trackFlowStep: (descriptor: FlowStepDescriptor) => void;
  trackHostedFlowStep: (
    step: HostedFlowTrackingStep,
    phase?: HostedFlowTrackingPhase
  ) => void;
  trackStepComplete: (stepName: string, stepIndex: number) => void;
  trackFormSubmit: () => void;
  getTrackingIds: () => Promise<TrackingIds>;
  trackEvent: (
    eventName: MixpanelEventName,
    properties?: Record<string, unknown>,
    options?: RequestOptions
  ) => void;
}

export const FormFunnelTrackingContext = createContext<FormFunnelTrackingContextType | undefined>(
  undefined
);

export const useFormFunnelTracking = () => {
  const context = useContext(FormFunnelTrackingContext);
  if (!context) {
    throw new Error("useFormFunnelTracking must be used within a FormFunnelTrackingProvider");
  }
  return context;
};
